import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCornexContactEnquiries } from "../../services/cornexQueryService";
import { toast } from "react-toastify";

const initialState = {
  enquiries: [],
  status: "idle",
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

/**
 * ===============================
 * GET: Fetch Cornex Contact Enquiries
 * ===============================
 */
export const fetchCornexEnquiries = createAsyncThunk(
  "cornexQuery/fetchEnquiries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getCornexContactEnquiries(params);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch enquiries";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const cornexQuerySlice = createSlice({
  name: "cornexQuery",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetCornexState: (state) => {
      state.enquiries = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCornexEnquiries.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCornexEnquiries.fulfilled, (state, action) => {
        state.status = "succeeded";

        /**
         * API response shape:
         * {
         *   success: true,
         *   data: {
         *     enquiries: [...],
         *     total: 8,
         *     page: 1,
         *     limit: 10
         *   }
         * }
         */
        const responseData = action.payload?.data || {};

        state.enquiries = responseData.enquiries || [];

        const total = responseData.total || 0;
        const page  = responseData.page  || 1;
        const limit = responseData.limit || 10;

        state.pagination = {
          total,
          page,
          limit,
          totalPages: Math.ceil(total / limit),
        };

        state.error = null;
      })
      .addCase(fetchCornexEnquiries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.enquiries = [];
        state.pagination = {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        };
      });
  },
});

export const { clearError, resetCornexState } = cornexQuerySlice.actions;

/**
 * ===============================
 * SELECTORS
 * ===============================
 */
export const selectAllCornexEnquiries = (state) =>
  state.cornexQuery.enquiries;

export const selectCornexEnquiryById = (state, id) =>
  state.cornexQuery.enquiries.find((item) => item._id === id);

export const selectCornexStatus   = (state) => state.cornexQuery.status;
export const selectCornexError    = (state) => state.cornexQuery.error;
export const selectCornexPagination = (state) => state.cornexQuery.pagination;

export default cornexQuerySlice.reducer;